import { useSelector } from "react-redux";
import clsx from "clsx";
import {
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
} from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { DR_DRIVER_TYPE, ROBOT_DRIVER_TYPE } from "../../../consts/driverTypes";
import { ROBOT_VEHICLE_TYPE } from "../../../consts/robot";
import { NONE } from "../../../consts/strings";
import { palette } from "../../../theme/palette";

import { formatDateToCorrectTimeZone } from "../../../utils/date";

import { vehiclesSelector } from "../../../reducers/driverManagement/selectors";

import { View } from "../../View";
import { DriverProfileLocationContent } from "./DriverProfileLocationContent";

const useStyles = makeStyles(() => ({
  form: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    gap: "1.5rem",
    padding: "1rem",
    minWidth: "48rem",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: "1.5rem",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "0.75rem",
    marginTop: "1rem",
  },
  formLabel: {
    fontSize: "0.9rem",
  },
  formDropDown: {
    width: "200px",
  },
  robotTypeCodeDropDown: {
    marginLeft: "116px",
  },
  formCheckbox: { paddingLeft: "0.5rem" },
  formCheckboxLabel: {
    color: palette.grey,
    fontSize: "13px",
  },
}));

export const DriverProfileModalContent = ({
  disabled,
  allowVehicleTypeChange = true,
  showAlternativeLocation = false,
  driverTypes,
  driverProfileEdit,
  onChange = async () => {},
  errors = {},
}) => {
  const styles = useStyles();
  const vehicles = useSelector(vehiclesSelector);

  const {
    driverType,
    robotTypeCode,
    robotTypeDescription,
    phoneNumber,
    vehicleType,
    securityCheckDate,
    saturnLockdown,
    notAtHomeSameDay,
  } = driverProfileEdit;

  const onProfileChange = async ({ target: { name, value } }) =>
    await onChange({ updates: [{ name, value }] });

  const onProfileChanges = async ({ updates = [] }) =>
    await onChange({ updates });

  const renderVehicleItems = () => {
    if (driverType === DR_DRIVER_TYPE)
      return [
        <MenuItem key={"ODF Vehicle"} value={NONE}>
          ODF Vehicle
        </MenuItem>,
      ];
    else if (driverType === ROBOT_DRIVER_TYPE)
      return [
        <MenuItem key={"Robot"} value={ROBOT_VEHICLE_TYPE}>
          Robot
        </MenuItem>,
      ];
    else
      return [
        <MenuItem key={NONE} value={NONE}>
          {NONE}
        </MenuItem>,
        ...Object.values(vehicles).map(({ code, description }) => (
          <MenuItem key={`${code}-${description}`} value={code}>
            {description}
          </MenuItem>
        )),
      ];
  };

  return (
    <FormControl className={styles.form} size="medium">
      <View className={styles.row}>
        <TextField
          variant="outlined"
          InputLabelProps={{ className: styles.formLabel, shrink: true }}
          onChange={onProfileChange}
          value={phoneNumber || ""}
          name="phoneNumber"
          label="Phone Number"
          id="driver-phone"
          error={!!errors["phoneNumber"]}
          helperText={errors["phoneNumber"]}
          disabled={disabled}
        />
        <TextField
          variant="outlined"
          className={styles.formDropDown}
          select
          label="Driver Type"
          onChange={onProfileChange}
          name="driverType"
          value={driverType}
          InputLabelProps={{ className: styles.formLabel, shrink: true }}
          error={!!errors["driverType"]}
          helperText={errors["driverType"]}
          disabled={disabled || driverTypes.length <= 1}
        >
          {driverTypes.map(({ value, label }) => (
            <MenuItem key={`${value}-${label}`} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          variant="outlined"
          className={styles.formDropDown}
          select
          onChange={onProfileChange}
          name="vehicleType"
          label="Vehicle Type"
          value={vehicleType || NONE}
          InputLabelProps={{ className: styles.formLabel, shrink: true }}
          error={!!errors["vehicleType"]}
          helperText={errors["vehicleType"]}
          disabled={disabled || !allowVehicleTypeChange}
        >
          {renderVehicleItems()}
        </TextField>
        <TextField
          variant="outlined"
          label="Security Check"
          type="date"
          onChange={onProfileChange}
          name="securityCheckDate"
          value={securityCheckDate || ""}
          InputProps={{
            inputProps: {
              max: formatDateToCorrectTimeZone(new Date()),
            },
          }}
          InputLabelProps={{ className: styles.formLabel, shrink: true }}
          disabled={disabled}
        />
      </View>
      <View className={styles.row}>
        <FormControlLabel
          className={styles.formCheckbox}
          control={
            <Checkbox
              color={"default"}
              checked={saturnLockdown}
              name="saturnLockdown"
              onChange={(e) =>
                onProfileChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: "saturnLockdown",
                    value: !!e.target.checked,
                  },
                })
              }
              disabled={disabled}
            />
          }
          label={
            <Typography className={styles.formCheckboxLabel}>
              Lockdown
            </Typography>
          }
        />
        <FormControlLabel
          className={styles.formCheckbox}
          control={
            <Checkbox
              color={"default"}
              checked={notAtHomeSameDay}
              name="notAtHomeSameDay"
              onChange={(e) =>
                onProfileChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: "notAtHomeSameDay",
                    value: !!e.target.checked,
                  },
                })
              }
              disabled={disabled}
            />
          }
          label={
            <Typography className={styles.formCheckboxLabel}>
              Not at Home Straight to Shop
            </Typography>
          }
        />
        {driverType === ROBOT_DRIVER_TYPE && (
          <TextField
            variant="outlined"
            className={clsx(styles.formDropDown, styles.robotTypeCodeDropDown)}
            select
            onChange={onProfileChange}
            name="robotTypeCode"
            label="Robot Type"
            value={robotTypeCode || NONE}
            InputLabelProps={{ className: styles.formLabel, shrink: true }}
            error={!!errors["robotTypeCode"]}
            helperText={errors["robotTypeCode"]}
            disabled={true}
          >
            {[
              <MenuItem key={NONE} value={NONE}>
                {NONE}
              </MenuItem>,
              <MenuItem key={robotTypeCode} value={robotTypeCode}>
                {robotTypeDescription}
              </MenuItem>,
            ]}
          </TextField>
        )}
      </View>
      {showAlternativeLocation && (
        <View className={styles.row}>
          <DriverProfileLocationContent
            disabled={disabled}
            driverProfileEdit={driverProfileEdit}
            onProfileChange={onProfileChange}
            onProfileChanges={onProfileChanges}
            errors={errors}
            locationType={"start"}
          />
          <DriverProfileLocationContent
            disabled={disabled}
            driverProfileEdit={driverProfileEdit}
            onProfileChange={onProfileChange}
            onProfileChanges={onProfileChanges}
            errors={errors}
            locationType={"end"}
          />
        </View>
      )}
    </FormControl>
  );
};
